import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Route, Router } from '@angular/router';

type ComponentLoader = () => Promise<unknown>;

@Injectable({ providedIn: 'root' })
export class RouteComponentPreloaderService implements OnDestroy {
  private started = false;
  private cancelled = false;
  private preloadTimer?: number;
  private idleCallbackId?: number;

  constructor(
    private readonly router: Router,
    private readonly ngZone: NgZone
  ) {}

  start(): void {
    if (this.started) return;

    const loaders = this.collectComponentLoaders(this.router.config);
    if (!loaders.length) return;

    this.started = true;
    this.cancelled = false;
    this.preloadNext(loaders);
  }

  ngOnDestroy(): void {
    this.cancelled = true;

    if (this.preloadTimer) {
      globalThis.clearTimeout(this.preloadTimer);
      this.preloadTimer = undefined;
    }

    if (this.idleCallbackId && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.idleCallbackId);
      this.idleCallbackId = undefined;
    }
  }

  private collectComponentLoaders(routes: Route[]): ComponentLoader[] {
    const loaders = new Map<string, ComponentLoader>();

    const visit = (routeList: Route[]): void => {
      for (const route of routeList) {
        if (route.loadComponent) {
          const key = route.path ?? route.loadComponent.toString();
          loaders.set(key, route.loadComponent as ComponentLoader);
        }

        if (route.children?.length) {
          visit(route.children);
        }
      }
    };

    visit(routes);
    return [...loaders.values()];
  }

  private preloadNext(loaders: ComponentLoader[]): void {
    if (this.cancelled || !loaders.length) return;

    const run = (): void => {
      if (this.cancelled) return;

      const loader = loaders.shift();
      if (!loader) return;

      this.ngZone.runOutsideAngular(() => {
        void loader()
          .catch(() => {
            // Route navigation still owns user-facing error handling. Background
            // preloading should never block the active page.
          })
          .finally(() => {
            this.preloadTimer = globalThis.setTimeout(() => this.preloadNext(loaders), 150);
          });
      });
    };

    if ('requestIdleCallback' in window) {
      this.idleCallbackId = window.requestIdleCallback(run, { timeout: 1500 });
      return;
    }

    this.preloadTimer = globalThis.setTimeout(run, 250);
  }
}
